import { useFinchConnect } from '@tryfinch/react-connect'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { usePostPayrollFinchCreateReauthenticationSession } from 'utils/web_api'

import { useCreateFinchConnectSession } from './useCreateFinchConnectSession'
import { useExchangeFinchAuthorizationCodeForToken } from './useExchangeFinchAuthorizationCodeForToken'

type ProviderCapabilities = {
  supportsAutomatedDeductions: boolean;
  supportsPaySchedules: boolean;
}
type OnSuccessCallback = (providerCapabilities: ProviderCapabilities) => void

interface UseFinchIntegrationProps {
  onSuccessCallback: OnSuccessCallback;
}

export function useFinchIntegration ({
  onSuccessCallback
}: UseFinchIntegrationProps) {
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [providerIdToOpen, setProviderIdToOpen] = useState<string | undefined>(undefined)
  const [providerSupportsAutomatedDeductions, setProviderSupportsAutomatedDeductions] = useState<boolean>(false)

  const createSessionMutation = useCreateFinchConnectSession()
  const createReauthSessionMutation = usePostPayrollFinchCreateReauthenticationSession({
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })
  const exchangeTokenMutation = useExchangeFinchAuthorizationCodeForToken()

  // Use a ref to track the latest value of providerSupportsAutomatedDeductions in callbacks
  const providerSupportsAutomatedDeductionsRef = useRef(providerSupportsAutomatedDeductions)
  providerSupportsAutomatedDeductionsRef.current = providerSupportsAutomatedDeductions

  const { open } = useFinchConnect({
    onSuccess: ({ code }) => {
      exchangeTokenMutation.mutate({ code }, {
        onSuccess: () => {
          onSuccessCallback({
            supportsAutomatedDeductions: providerSupportsAutomatedDeductionsRef.current,
            supportsPaySchedules: true
          })
        },
        onError: () => {
          toast.error('Error connecting to payroll provider, please try again or contact support.')
        }
      })
    },
    onError: (e) => console.error(e),
    onClose: () => {}
  })

  const startConnection = async (providerId: string, supportsAutomatedDeductions = false) => {
    setProviderIdToOpen(providerId)
    setProviderSupportsAutomatedDeductions(supportsAutomatedDeductions)

    return new Promise<void>((resolve) => {
      createSessionMutation.mutate({ payrollProviderConfigId: providerId }, {
        onSuccess: ({ session_id: sessionId }) => {
          setSessionId(sessionId)
          resolve()
        },
        onError: () => {
          toast.error('Error connecting to payroll provider, please try again.')
          resolve()
        }
      })
    })
  }

  const startReauthentication = async () => {
    return new Promise<void>((resolve) => {
      createReauthSessionMutation.mutate(undefined, {
        onSuccess: (response: any) => {
          setSessionId(response.session_id)
          resolve()
        },
        onError: () => {
          toast.error('Error starting reauthentication, please try again.')
          resolve()
        }
      })
    })
  }

  const openModal = () => {
    if (sessionId) {
      open({ sessionId })
    }
  }

  return {
    startConnection,
    startReauthentication,
    openModal,
    sessionId,
    providerIdToOpen,
    setProviderIdToOpen
  }
}
